import { MedicalRecordFormType } from 'models/MedicalRecord/MedicalRecordForm.type'
import { InferType, object, string, array, number } from 'yup'
import { NONE_MEDICATION_OPTION_ID } from './types/option'

const OTHERS_LABEL = 'Outros(as)'

const isOtherRequired = (items: { description: string }[]): boolean => {
  if (!items?.length) return
  return items.some((item) => item.description === OTHERS_LABEL)
}

const isOtherPatientConsumptionRequired = (items: string[]): boolean => {
  if (!items?.length) return
  return items.some((item) => item === 'patient-consumption-others')
}

export const createMedicalRecordSchema = (formType: MedicalRecordFormType) => {
  const commonFields = {
    type: string().required(),
    complaints: array()
      .of(
        object({
          id: number(),
          name: string(),
          ciapCode: string(),
          description: string(),
        })
      )
      .min(1, 'Preencha o campo')
      .required('Preencha o campo'),
    symptoms: array()
      .of(
        object({
          id: number(),
          name: string(),
          description: string(),
        })
      )
      .min(1, 'Preencha o campo')
      .required('Preencha o campo'),
    medications: array()
      .of(
        object({
          id: number(),
          name: string(),
          commercialName: string(),
          description: string(),
          medication_medical_record_form_id: number().optional().nullable(),
          status: object({
            id: string(),
            label: string(),
          }).when('id', {
            is: NONE_MEDICATION_OPTION_ID,
            then: object({
              id: string(),
              label: string(),
            }).nullable().default(undefined),
            otherwise: object({
              id: string(),
              label: string(),
            }).required('Preencha o campo').default(undefined),
          }),
        })
      )
      .min(1, 'Preencha o campo')
      .required('Preencha o campo'),
    diagnostic_hypotheses: array()
      .of(
        object({
          id: number(),
          name: string(),
          cidCode: string(),
          description: string(),
          diagnostic_hypothesis_medical_record_form_id: number().optional().nullable(),
          status: object({
            id: string(),
            label: string(),
          }).required('Preencha o campo').default(undefined),
        })
      )
      .min(1, 'Preencha o campo')
      .required('Preencha o campo'),
    patient_needs_to_continue_treatment: object({
      number_of_schedules: string().when('need_to_continue_treatment', {
        is: 'patient-needs-to-continue-treatment-no',
        then: string().optional().nullable(),
        otherwise: string(),
      }),
      reason: string().when('need_to_continue_treatment', {
        is: 'patient-needs-to-continue-treatment-no',
        then: string().optional().nullable(),
        otherwise: string(),
      }),
      other_reason: string().when('need_to_continue_treatment.reason', {
        is: 'OTHER',
        then: string().optional().nullable(),
        otherwise: string(),
      }),
      need_to_continue_treatment: string(),
    }).optional().nullable(),
    other_complaint: string().when('complaints', {
      is: isOtherRequired,
      then: string().required('Preencha o campo'),
      otherwise: string().optional().nullable(),
    }),
    other_symptom: string().when('symptoms', {
      is: isOtherRequired,
      then: string().required('Preencha o campo').nullable(),
      otherwise: string().optional().nullable(),
    }),
    other_medication: string().when('medications', {
      is: isOtherRequired,
      then: string().required('Preencha o campo'),
      otherwise: string().optional().nullable(),
    }),
    other_patient_consumption: string().when('patient_consumptions', {
      is: isOtherPatientConsumptionRequired,
      then: string().required('Preencha o campo').nullable(),
      otherwise: string().optional().nullable(),
    }),
    patient_consumptions: array().of(string()),
    symptoms_impact_on_work_life: string().required('Preencha o campo'),
    has_suicidal_ideation: string().required('Preencha o campo'),
    has_psychiatrist_treatment: string().required('Preencha o campo'),
    patient_goal: string().required('Preencha o campo'),
    patient_case_complexity: string().required('Preencha o campo'),
    last_patient_case_complexity: string().optional().nullable(),
    patient_case_description: string().required('Preencha o campo').nullable(),
    medical_professional_referral: string().nullable(),
    files: array().of(object({
      uuid: string(),
      original_name: string(),
      size: number(),
      url: string(),
    })),
  }

  const technicalEvaluationFields = {
    ...commonFields,
  }

  const evolutionFields = {
    ...commonFields,
    complaints: commonFields.complaints.optional().nullable().min(0),
    symptoms: commonFields.symptoms.optional().nullable().min(0),
    has_psychiatrist_treatment: commonFields.has_psychiatrist_treatment.optional().nullable(),
    patient_goal: commonFields.patient_goal.optional().nullable(),
    symptoms_impact_on_work_life: commonFields.symptoms_impact_on_work_life.optional().nullable(),
    patient_needs_to_continue_treatment: commonFields.patient_needs_to_continue_treatment.optional().nullable(),
  }

  const outcomeFields = {
    ...commonFields,
    patient_needs_to_continue_treatment: object({
      number_of_schedules: string().when('need_to_continue_treatment', {
        is: 'patient-needs-to-continue-treatment-no',
        then: string().optional().nullable(),
        otherwise: string().required('Preencha o campo'),
      }),
      reason: string().when('need_to_continue_treatment', {
        is: 'patient-needs-to-continue-treatment-no',
        then: string().required('Preencha o campo'),
        otherwise: string().optional().nullable(),
      }),
      other_reason: string().when('reason', {
        is: 'OTHER',
        then: string().required('Preencha o campo'),
        otherwise: string().optional().nullable(),
      }),
      need_to_continue_treatment: string().required('Preencha o campo'),
    }),
  }

  switch (formType) {
  case 'TECHNICAL_EVALUATION':
    return object(technicalEvaluationFields)
  case 'EVOLUTION':
    return object(evolutionFields)
  case 'OUTCOME':
    return object(outcomeFields)
  default:
    throw new Error('Invalid form type')
  }
}

export const medicalRecordSchema = createMedicalRecordSchema(
  MedicalRecordFormType.TECHNICAL_EVALUATION
)

export type MedicalRecordFormData = InferType<typeof medicalRecordSchema>
